import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/layout/TopBar';
import api from '../utils/api';
import toast from 'react-hot-toast';

const timeSince = (d) => {
  const diff = Date.now() - new Date(d);
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const CommunityPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('feed');
  const [showPost, setShowPost] = useState(false);
  const [postForm, setPostForm] = useState({ content: '', horse_id: '', post_type: 'general' });
  const [horses, setHorses] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [feedRes, horsesRes] = await Promise.all([
          api.get('/community/feed'),
          api.get('/horses'),
        ]);
        setPosts(feedRes.data);
        setHorses(horsesRes.data);
      } catch { toast.error('Failed to load feed'); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const handleLike = async (post) => {
    try {
      const { data } = await api.post(`/community/posts/${post.id}/like`);
      setPosts(prev => prev.map(p => p.id === post.id
        ? { ...p, likes_count: data.liked ? p.likes_count + 1 : p.likes_count - 1, liked: data.liked }
        : p));
    } catch {}
  };

  const handlePost = async () => {
    if (!postForm.content.trim()) return toast.error('Write something first');
    try {
      const { data } = await api.post('/community/posts', { ...postForm, horse_id: postForm.horse_id || null });
      setPosts(prev => [data, ...prev]);
      setShowPost(false);
      setPostForm({ content: '', horse_id: '', post_type: 'general' });
      toast.success('Posted!');
    } catch { toast.error('Failed to post'); }
  };

  const PostCard = ({ post }) => (
    <div style={{ background: 'white', borderBottom: '1px solid var(--border-color)', marginBottom: 0 }}>
      <div style={{ display: 'flex', gap: 10, padding: '12px 14px 8px' }}>
        <div className="avatar avatar-sm" style={{ flexShrink: 0, background: 'var(--cr-green-light)' }}>
          {post.author?.avatar_url
            ? <img src={post.author.avatar_url} alt="" />
            : <span style={{ fontSize: 13 }}>{post.author?.first_name?.[0]}{post.author?.last_name?.[0]}</span>}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{post.author?.first_name} {post.author?.last_name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            {post.horse && <span style={{ color: 'var(--cr-green-dark)' }}>🐴 {post.horse.name} · </span>}
            {timeSince(post.created_at)}
          </div>
        </div>
      </div>
      {post.content && <div style={{ padding: '0 14px 8px', fontSize: 13, lineHeight: 1.6 }}>{post.content}</div>}
      {post.media_urls?.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: post.media_urls.length > 1 ? '1fr 1fr' : '1fr', gap: 2, margin: '0 0 8px' }}>
          {post.media_urls.slice(0, 4).map((url, i) => (
            <img key={i} src={url} alt="" style={{ width: '100%', height: 160, objectFit: 'cover' }} />
          ))}
        </div>
      )}
      {post.tagged_product_name && (
        <div style={{ padding: '4px 14px 8px', display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Tagged:</span>
          <a href={post.tagged_product_url || '#'} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--cr-green)', textDecoration: 'none' }}>{post.tagged_product_name} — shop it</a>
        </div>
      )}
      <div style={{ display: 'flex', gap: 16, padding: '8px 14px 12px', borderTop: '1px solid var(--border-color)' }}>
        <button onClick={() => handleLike(post)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: post.liked ? 'var(--cr-pink)' : 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
          {post.liked ? '❤️' : '🤍'} {post.likes_count || 0}
        </button>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 4 }}>
          💬 {post.comments_count || 0}
        </button>
        <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', marginLeft: 'auto' }}>
          ↗️ Share
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <TopBar title="Community"
        rightAction={
          <button className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: 'none' }}
            onClick={() => setShowPost(true)}>+ Post</button>
        }
      />

      <div className="tab-bar">
        <div className={`tab-item ${tab === 'feed' ? 'active' : ''}`} onClick={() => setTab('feed')}>Feed</div>
        <div className={`tab-item ${tab === 'messages' ? 'active' : ''}`} onClick={() => setTab('messages')}>Messages</div>
        <div className={`tab-item ${tab === 'marketplace' ? 'active' : ''}`} onClick={() => navigate('/marketplace')}>Marketplace</div>
      </div>

      {tab === 'feed' && (
        <div style={{ paddingBottom: 80 }}>
          {loading && <div className="loading-screen"><div className="spinner" /></div>}
          {!loading && posts.length === 0 && (
            <div className="empty-state" style={{ padding: '40px 20px' }}>
              <div className="empty-state-icon">🐴</div>
              <h3>Your feed is empty</h3>
              <p>Follow horses and riders to see their posts here, or make your first post</p>
              <button className="btn btn-primary" onClick={() => setShowPost(true)}>Make your first post</button>
            </div>
          )}
          {!loading && posts.map(post => <PostCard key={post.id} post={post} />)}
        </div>
      )}

      {tab === 'messages' && (
        <div className="page-content">
          <div className="empty-state">
            <div className="empty-state-icon">💬</div>
            <h3>Messages</h3>
            <p>Direct messages with riders, trainers, and barn mates coming soon</p>
          </div>
        </div>
      )}

      {/* Post modal */}
      {showPost && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999, display: 'flex', alignItems: 'flex-end' }}>
          <div style={{ background: 'white', borderRadius: '20px 20px 0 0', padding: 20, width: '100%', maxWidth: 480, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 14 }}>
              <h2>New post</h2>
              <button onClick={() => setShowPost(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer' }}>×</button>
            </div>
            {horses.length > 0 && (
              <div className="form-group">
                <label className="form-label">Post for horse (optional)</label>
                <div className="chip-group" style={{ marginBottom: 10 }}>
                  <div className={`chip ${!postForm.horse_id ? 'selected' : ''}`} onClick={() => setPostForm(p => ({ ...p, horse_id: '' }))}>My profile</div>
                  {horses.map(h => <div key={h.id} className={`chip ${postForm.horse_id === h.id ? 'selected' : ''}`} onClick={() => setPostForm(p => ({ ...p, horse_id: h.id }))}>{h.name}</div>)}
                </div>
              </div>
            )}
            <div className="form-group">
              <textarea className="form-input" rows={4} placeholder="What's happening at the barn today? 🐴" value={postForm.content} onChange={e => setPostForm(p => ({ ...p, content: e.target.value }))} autoFocus />
            </div>
            <button className="btn btn-primary btn-full" onClick={handlePost}>Post</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;
